import React, { useRef, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { PROPERTY_GALLERY } from "../mock";

const PropertyGallery = () => {
  const trackRef = useRef(null);
  const [lightbox, setLightbox] = useState(null);

  const scrollBy = useCallback((dir) => {
    const el = trackRef.current;
    if (!el) return;
    const amount = Math.round(el.clientWidth * 0.8);
    el.scrollBy({ left: dir * amount, behavior: "smooth" });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  const showPrev = useCallback(
    (e) => {
      e.stopPropagation();
      setLightbox((i) => (i > 0 ? i - 1 : PROPERTY_GALLERY.length - 1));
    },
    []
  );
  const showNext = useCallback(
    (e) => {
      e.stopPropagation();
      setLightbox((i) => (i < PROPERTY_GALLERY.length - 1 ? i + 1 : 0));
    },
    []
  );

  return (
    <section id="gallery" className="py-20 md:py-28 bg-white" data-testid="property-gallery">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <p className="text-xs font-semibold tracking-[0.25em] uppercase text-[#17a89b] mb-4">
              Property Tour
            </p>
            <h2 className="text-3xl md:text-5xl font-extrabold text-neutral-900 leading-tight">
              A Glimpse of Your Stay
            </h2>
            <p className="text-neutral-600 max-w-2xl mt-4 leading-8">
              Explore our A-frame cottages, spacious rooms and thoughtfully finished interiors —
              nestled amid the greenery near Anachal, Munnar.
            </p>
          </div>
          <div className="hidden md:flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              aria-label="Scroll gallery left"
              data-testid="gallery-prev-btn"
              className="w-12 h-12 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-700 hover:bg-[#17a89b] hover:text-white hover:border-[#17a89b] transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={() => scrollBy(1)}
              aria-label="Scroll gallery right"
              data-testid="gallery-next-btn"
              className="w-12 h-12 rounded-full border border-neutral-300 flex items-center justify-center text-neutral-700 hover:bg-[#17a89b] hover:text-white hover:border-[#17a89b] transition-colors"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={trackRef}
        className="flex gap-4 md:gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth px-5 md:px-8 max-w-7xl mx-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        data-testid="gallery-track"
      >
        {PROPERTY_GALLERY.map((img, i) => (
          <button
            type="button"
            key={img.src}
            onClick={() => setLightbox(i)}
            data-testid={`gallery-item-${i}`}
            className="group relative shrink-0 snap-start w-[80%] sm:w-[46%] lg:w-[31%] aspect-[4/3] overflow-hidden rounded-2xl bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-[#17a89b] focus:ring-offset-2"
          >
            <img
              src={img.src}
              alt={img.alt}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4 pt-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <span className="text-white text-sm font-semibold tracking-wide">
                {img.caption}
              </span>
            </div>
          </button>
        ))}
      </div>

      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={closeLightbox}
          data-testid="gallery-lightbox"
        >
          <button
            type="button"
            onClick={closeLightbox}
            aria-label="Close"
            data-testid="lightbox-close-btn"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={showPrev}
            aria-label="Previous image"
            data-testid="lightbox-prev-btn"
            className="absolute left-4 md:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <figure className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={PROPERTY_GALLERY[lightbox].src}
              alt={PROPERTY_GALLERY[lightbox].alt}
              className="w-full max-h-[80vh] object-contain rounded-lg"
            />
            <figcaption className="text-center text-white/90 text-sm mt-4 font-medium tracking-wide">
              {PROPERTY_GALLERY[lightbox].caption} · {lightbox + 1} / {PROPERTY_GALLERY.length}
            </figcaption>
          </figure>
          <button
            type="button"
            onClick={showNext}
            aria-label="Next image"
            data-testid="lightbox-next-btn"
            className="absolute right-4 md:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        </div>
      )}
    </section>
  );
};

export default PropertyGallery;
